import { useMemo, useRef, useState } from 'react'
import { CheckCircle2, FileSpreadsheet, TriangleAlert, Upload } from 'lucide-react'
import {
  Bidi, Card, CardHeader, ErrorState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, commitRoomLoad, formatCount, may, previewRoomLoad, translateError,
  type LoadPlan, type LoadProblem, type PlannedRoom, type PlannedRoomType,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-127 · LOADING THE HOTEL'S OWN ROOMS.

   THIS IS THE SCREEN THAT DECIDES WHETHER GO-LIVE HAPPENS. Everything else in this
   product edits data that is already here; this puts five hundred rooms in, on a
   Thursday, before anybody has used the software. When it fails it does not produce a
   bug report — it produces a go-live that does not happen.

   THREE THINGS IT IS BUILT AROUND, all of them consequences of that:

   1. **LOOK, THEN ACT.** The upload previews and changes nothing. A person agrees to
      five hundred rooms on the strength of what this screen says, and cannot undo it.
   2. **EVERY PROBLEM AT ONCE, AGAINST A LINE NUMBER.** They have the spreadsheet open
      beside them. "Line 62: room 305 is already on line 14" is a fix; "invalid data" is
      a telephone call.
   3. **THE NUMBER THAT IS NOT A CONGRATULATION.** "Rooms in the hotel this file does not
      mention" is drawn as prominently as the rooms being added, because a partial export
      looks exactly like a good load until somebody notices four hundred rooms missing
      from the board on Monday.

   The file is read on the SERVER, not here. A parser in the browser would be a second
   implementation of what a valid file is, and the two would disagree — quietly, on the
   one file that mattered.
   ========================================================================= */

export function RoomLoad({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [file, setFile] = useState<File | null>(null)
  const [plan, setPlan] = useState<LoadPlan | null>(null)
  const [busy, setBusy] = useState<'reading' | 'loading' | null>(null)
  const [error, setError] = useState<unknown>(null)
  const input = useRef<HTMLInputElement>(null)

  if (!may('admin.rooms')) return <NoPermissionState />

  async function choose(chosen: File | null) {
    if (!chosen) return
    setFile(chosen); setPlan(null); setError(null); setBusy('reading')
    try {
      setPlan(await previewRoomLoad(propertyId, chosen))
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function commit() {
    if (!file || !plan?.canCommit) return
    setBusy('loading'); setError(null)
    try {
      setPlan(await commitRoomLoad(propertyId, file))
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.loadTitle} icon={<FileSpreadsheet className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[62ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.loadIntro}
          </p>

          {/* THE COLUMNS IT NEEDS, ON THE SCREEN. A person about to upload a file wants
              to know what it must contain BEFORE they are told it is wrong. */}
          <p className="max-w-[62ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.loadColumns}
          </p>

          <div className="flex flex-wrap items-center gap-2.5">
            <input
              ref={input}
              type="file"
              /* .xlsx FIRST because it is the one to ask for: it stores text as UTF-8 and
                 Arabic arrives intact with nothing to configure. CSV is accepted because
                 people have it, and refused by the server when it is not Unicode. */
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(e) => void choose(e.target.files?.[0] ?? null)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => input.current?.click()}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                         px-3 py-1.5 text-xs font-medium text-white
                         disabled:opacity-60 cursor-pointer"
            >
              <Upload className="size-3.5" />
              {file ? t.loadChooseAnother : t.loadChoose}
            </button>

            {file && (
              <span className="text-[12.5px] text-muted-foreground">
                <Bidi>{file.name}</Bidi>
              </span>
            )}
            {busy === 'reading' && (
              <span className="text-[12.5px] text-muted-foreground">{t.loadReading}</span>
            )}
          </div>
        </div>
      </Card>

      {error !== null && <Refusal error={error} locale={locale} onRetry={() => setError(null)} />}

      {plan?.applied && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-hk border border-[#86efac] bg-[#f0fdf4]
                     px-3.5 py-3 text-[12.5px] text-[#166534]
                     dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]"
        >
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <span>{t.loadDone
            .replace('{rooms}', formatCount(plan.counts.roomsNew, locale))
            .replace('{moved}', formatCount(plan.counts.roomsMoved, locale))}</span>
        </div>
      )}

      {plan && !plan.applied && <Summary plan={plan} locale={locale} t={t} onCommit={commit} busy={busy === 'loading'} />}
      {plan && plan.problems.length > 0 && <Problems problems={plan.problems} locale={locale} t={t} />}
      {plan && plan.roomTypes.length > 0 && (
        <RoomTypes types={plan.roomTypes} locale={locale} t={t} />
      )}
      {plan && plan.rooms.length > 0 && <Rooms rooms={plan.rooms} locale={locale} t={t} />}
    </div>
  )
}

/* ── what would happen ────────────────────────────────────────────────────── */

function Summary({
  plan, locale, t, onCommit, busy,
}: {
  plan: LoadPlan; locale: 'en' | 'ar'; t: typeof STRINGS['en']
  onCommit: () => void; busy: boolean
}) {
  const n = (value: number) => formatCount(value, locale)

  return (
    <Card>
      <CardHeader title={t.loadWouldHappen} icon={<FileSpreadsheet className="size-4" />} />
      {/* NO FIGURES WHEN THE FILE CANNOT BE LOADED.

          Four zeros under the heading "what this would do" is technically true — nothing
          would happen — and reads as "your file has no rooms in it", which is a different
          and much more alarming statement. The server sends no counts for a file it
          refuses, and drawing them as zero puts a number on the screen that nobody
          measured. The problems below are the whole message. */}
      {plan.canCommit && (
      <div className="grid gap-2.5 p-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label={t.loadNewRooms} value={n(plan.counts.roomsNew)} />
        <Figure label={t.loadMovedRooms} value={n(plan.counts.roomsMoved)} />
        <Figure label={t.loadUnchangedRooms} value={n(plan.counts.roomsUnchanged)} />
        {/* DRAWN AS LOUDLY AS THE REST. A partial export looks exactly like a good load
            until somebody notices four hundred rooms missing from the board on Monday. */}
        <Figure
          label={t.loadNotInFile}
          value={n(plan.counts.roomsNotInFile)}
          tone={plan.counts.roomsNotInFile > 0 ? 'warn' : 'plain'}
          note={plan.counts.roomsNotInFile > 0 ? t.loadNotInFileNote : undefined}
        />
      </div>
      )}

      {plan.canCommit && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3.5 py-2
                      text-[12px] text-muted-foreground">
        <span>{t.loadFloorsAndTypes
          .replace('{floors}', n(plan.counts.floorsNew))
          .replace('{types}', n(plan.counts.typesNew))
          .replace('{changed}', n(plan.counts.typesChanged))}</span>
        {/* SAID ONLY WHEN IT HAPPENS. A hotel of one building creates no buildings, and
            "0 new buildings" on every load is a line nobody reads — which is how the one
            load that quietly invents a fifth tower goes unnoticed. */}
        {plan.counts.buildingsNew > 0 && (
          <span>{t.loadBuildingsNew.replace('{count}', n(plan.counts.buildingsNew))}</span>
        )}
        {plan.counts.roomsLeftOutOfUse > 0 && (
          <span>{t.loadLeftOutOfUse.replace('{count}', n(plan.counts.roomsLeftOutOfUse))}</span>
        )}
        {/* CONVERTING AN IDENTIFIER IS SAID OUT LOUD. ٤٠٤ and 404 are the same room and
            the mapping is not a guess — but a room number is an identifier, and changing
            one silently is not the same as changing one. */}
        {plan.convertedFromEasternDigits > 0 && (
          <span>{t.loadConverted.replace('{count}', n(plan.convertedFromEasternDigits))}</span>
        )}
      </div>
      )}

      <div className="flex items-center gap-3 border-t border-border px-3.5 py-3">
        <button
          type="button"
          onClick={onCommit}
          disabled={!plan.canCommit || busy}
          className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium text-white
                     disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed
                     cursor-pointer"
        >
          {busy ? t.loadLoading : t.loadCommit}
        </button>
        {!plan.canCommit && (
          /* ALL OR NOTHING, and the button says why it is shut rather than just being
             grey. Loading the good rows would leave a hotel in a state nobody can
             describe. */
          <span className="text-[12.5px] text-muted-foreground">{t.loadFixFirst}</span>
        )}
      </div>
    </Card>
  )
}

function Figure({
  label, value, tone = 'plain', note,
}: { label: string; value: string; tone?: 'plain' | 'warn'; note?: string }) {
  return (
    <div
      className={`rounded-hk border p-2.5
                  ${tone === 'warn'
                    ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#92400e] dark:bg-[#2a1f07]'
                    : 'border-border'}`}
    >
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-[19px] font-semibold tabular-nums">{value}</div>
      {note && <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{note}</div>}
    </div>
  )
}

/* ── what is wrong with it ────────────────────────────────────────────────── */

function Problems({
  problems, locale, t,
}: { problems: LoadProblem[]; locale: 'en' | 'ar'; t: typeof STRINGS['en'] }) {
  return (
    <Card>
      <CardHeader
        title={t.loadProblems.replace('{count}', formatCount(problems.length, locale))}
        icon={<TriangleAlert className="size-4 text-[#b45309]" />}
      />
      <div className="max-h-[22rem] overflow-y-auto">
        <table className="w-full border-collapse text-[12.5px]">
          <tbody>
            {problems.map((problem, index) => (
              <tr key={`${problem.line}-${problem.code}-${index}`} className="border-b border-border last:border-0">
                {/* THE LINE NUMBER IS THE POINT OF THE WHOLE TABLE, so it leads the row
                    and is drawn as an identifier — Western digits, monospaced, because it
                    is matched against what Excel shows down the side of the page. */}
                <td className="w-24 p-2 align-top font-mono text-[12px] text-muted-foreground">
                  {problem.line > 0 ? t.loadLine.replace('{line}', String(problem.line)) : ''}
                </td>
                <td className="p-2 align-top">
                  <Bidi>{messageFor(problem, locale)}</Bidi>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/**
 * A row problem, as a sentence.
 *
 * It goes through the SAME catalogue as every other error in the product — these are
 * ordinary error codes with details, and an administrator reading Arabic gets Arabic.
 * Building the sentence here from the code would be a second catalogue, in one screen,
 * that nothing translates.
 */
function messageFor(problem: LoadProblem, locale: 'en' | 'ar'): string {
  const details: Record<string, string> = {}
  if (problem.value !== null) details['value'] = problem.value
  if (problem.other !== null) details['other'] = problem.other

  return translateError(
    new ApiError({ code: problem.code, details, correlationId: '' }, 400), locale).message
}

/* ── the kinds of room in it ──────────────────────────────────────────────── */

/**
 * **WHAT THE FILE SAYS EACH KIND OF ROOM IS** — and above all, **how many people it
 * sleeps.** Sprint 49.
 *
 * It was a count in one line before ("2 new room types · 1 renamed"), which is enough
 * while a type is only a name. It stopped being enough the moment a type carried a NUMBER
 * that decides how much linen goes through every one of its doors: a file that quietly
 * says a four-person room sleeps two would have passed that line without a mark, and the
 * consequence is two towels short in four hundred rooms, every day, for ever.
 *
 * So the types are drawn in full and the capacity is a column. **The moment to disagree
 * with the number is before the load.**
 */
function RoomTypes({
  types, locale, t,
}: { types: PlannedRoomType[]; locale: 'en' | 'ar'; t: typeof STRINGS['en'] }) {
  return (
    <Card>
      <CardHeader
        title={t.loadTypes.replace('{count}', formatCount(types.length, locale))}
        icon={<FileSpreadsheet className="size-4" />}
      />
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-border text-[11px] text-muted-foreground">
            <th className="p-2 text-start font-medium">{t.loadColType}</th>
            <th className="p-2 text-start font-medium">{t.loadColTypeName}</th>
            <th className="p-2 text-start font-medium">{t.loadColSleeps}</th>
            <th className="p-2 text-start font-medium">{t.loadColMinutes}</th>
            <th className="p-2 text-start font-medium">{t.loadColRooms}</th>
            <th className="p-2 text-start font-medium">{t.loadColChange}</th>
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type.code} className="border-b border-border last:border-0">
              <td className="p-2 font-mono font-semibold">{type.code}</td>
              <td className="p-2">
                <Bidi>{locale === 'ar' ? type.nameAr ?? type.name : type.name}</Bidi>
              </td>
              <td className="p-2 tabular-nums">{formatCount(type.capacity, locale)}</td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {formatCount(type.cleanMinutes, locale)}
              </td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {formatCount(type.rooms, locale)}
              </td>
              <td className="p-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] ${TONE[type.change]}`}>
                  {t.loadChanges[type.change]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

/* ── every room in the file ───────────────────────────────────────────────── */

function Rooms({
  rooms, locale, t,
}: { rooms: PlannedRoom[]; locale: 'en' | 'ar'; t: typeof STRINGS['en'] }) {
  /* **THE BUILDING COLUMN APPEARS WHEN THERE IS MORE THAN ONE BUILDING**, and not before.
     A hotel with one building would otherwise get four hundred rows all reading MAIN — a
     column of identical cells that says nothing and pushes the ones that do say something
     off the side of the page. The moment a file names two, the column is the difference
     between "3 → 3" meaning nothing and meaning a room has changed tower. */
  const showBuilding = useMemo(() => {
    const named = new Set<string>()
    for (const room of rooms) {
      if (room.building) named.add(room.building.toUpperCase())
      if (room.wasBuilding) named.add(room.wasBuilding.toUpperCase())
    }
    return named.size > 1
  }, [rooms])

  /* THE ROWS THAT DO SOMETHING COME FIRST. Four hundred and ninety-two unchanged rooms
     are the least interesting thing on the page and would otherwise bury the eight that
     are not — and somebody scrolling a table of five hundred rows to find them will
     scroll past them. */
  const ordered = useMemo(() => {
    const rank: Record<string, number> = { NEW: 0, CHANGED: 1, LEFT_OUT_OF_USE: 2, UNCHANGED: 3 }
    return [...rooms].sort((a, b) =>
      (rank[a.change] ?? 9) - (rank[b.change] ?? 9) || a.line - b.line)
  }, [rooms])

  return (
    <Card>
      <CardHeader
        title={t.loadRooms.replace('{count}', formatCount(rooms.length, locale))}
        icon={<FileSpreadsheet className="size-4" />}
      />
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="w-full border-collapse text-[12.5px]">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border text-[11px] text-muted-foreground">
              <th className="p-2 text-start font-medium">{t.loadColRoom}</th>
              {showBuilding && (
                <th className="p-2 text-start font-medium">{t.loadColBuilding}</th>
              )}
              <th className="p-2 text-start font-medium">{t.loadColFloor}</th>
              <th className="p-2 text-start font-medium">{t.loadColType}</th>
              <th className="p-2 text-start font-medium">{t.loadColChange}</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((room) => (
              <tr key={room.line} className="border-b border-border last:border-0">
                {/* A room number is an IDENTIFIER — Western digits in both languages,
                    because it is painted on the door and read aloud. */}
                <td className="p-2 font-mono font-semibold">{room.roomNumber}</td>
                {showBuilding && (
                  <td className="p-2">
                    {room.wasBuilding && room.wasBuilding !== room.building
                      ? <span className="text-muted-foreground">
                          <span className="line-through"><Bidi>{room.wasBuilding}</Bidi></span>{' → '}
                          <span className="font-medium text-foreground"><Bidi>{room.building}</Bidi></span>
                        </span>
                      : <Bidi>{room.building}</Bidi>}
                  </td>
                )}
                <td className="p-2">
                  {room.wasFloor && room.wasFloor !== room.floor
                    ? <span className="text-muted-foreground">
                        <span className="line-through">{room.wasFloor}</span>{' → '}
                        <span className="font-medium text-foreground">{room.floor}</span>
                      </span>
                    : room.floor}
                </td>
                <td className="p-2">
                  {room.wasTypeCode && room.wasTypeCode !== room.typeCode
                    ? <span className="text-muted-foreground">
                        <span className="line-through">{room.wasTypeCode}</span>{' → '}
                        <span className="font-medium text-foreground">{room.typeCode}</span>
                      </span>
                    : room.typeCode}
                </td>
                <td className="p-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${TONE[room.change]}`}>
                    {t.loadChanges[room.change]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

const TONE: Record<string, string> = {
  NEW: 'bg-[#dcfce7] text-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]',
  CHANGED: 'bg-[#dbeafe] text-[#1e40af] dark:bg-[#0b1f3a] dark:text-[#93c5fd]',
  UNCHANGED: 'bg-muted text-muted-foreground',
  LEFT_OUT_OF_USE: 'bg-[#fef3c7] text-[#92400e] dark:bg-[#2a1f07] dark:text-[#fcd34d]',
}

function Refusal({
  error, locale, onRetry,
}: { error: unknown; locale: 'en' | 'ar'; onRetry: () => void }) {
  const translated = translateError(error, locale)
  return (
    <ErrorState
      body={translated.message}
      onRetry={onRetry}
      {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
    />
  )
}
